import { Amplify } from "aws-amplify";
import { ApolloProvider } from "@apollo/client/react";
import type { ReactNode } from "react";
import { ThemeProvider } from "styled-components";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCaretUp,
  faPen,
  faCheck,
  faCircle,
  faBell,
  faArrowLeft,
  faUserPlus,
  faFile,
  faChevronUp,
  faChevronDown,
  faChevronLeft,
  faChartBar,
  faExclamationCircle,
  faQuestionCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

import { useApolloClient } from "./hooks/useApolloClient";
import { defaultTheme } from "./components/themes";
import { GlobalStyle } from "./components/GlobalStyle";
import { Router } from "./Router";
import {
  openModal,
  initializeModalState,
  setNotFoundStatus,
  hideMenuList,
  setServerErrorStatus,
  setForbiddenStatus,
  setNotSameSubDomainStatus,
} from "./graphql/local-mutations";
import { AWS_AUTH_CONFIG } from "./settings";
import { AuthContext } from "./hooks/useAuthentication";
import { use } from "react";

Amplify.configure(AWS_AUTH_CONFIG);

const ErrorLinkHandler = ({
  isSessionExpired,
  isNotFound,
  isForbidden,
  isServerError,
  isNotSameSubDomain,
  children,
}: {
  isSessionExpired: boolean;
  isNotFound: boolean;
  isForbidden: boolean;
  isServerError: boolean;
  isNotSameSubDomain: boolean;
  children: ReactNode;
}) => {
  const { expiresSession } = use(AuthContext);
  if (isSessionExpired) {
    initializeModalState();
    expiresSession();
    openModal("sessionExpired");
  }

  if (isNotFound) {
    setNotFoundStatus(true);
  }

  if (isForbidden) {
    hideMenuList();
    setForbiddenStatus(true);
  }

  if (isServerError) {
    initializeModalState();
    hideMenuList();
    setServerErrorStatus(true);
  }

  if (isNotSameSubDomain) {
    setNotSameSubDomainStatus(true);
  }

  return <>{children}</>;
};

const ApolloProviderWithClient = ({ children }: { children: ReactNode }) => {
  const { apolloClient, errorLinkStatus } = useApolloClient();
  const ErrorLinkHandlerProps = {
    isSessionExpired: errorLinkStatus.sessionExpired,
    isNotFound: errorLinkStatus.notFound,
    isForbidden: errorLinkStatus.forbidden,
    isServerError: errorLinkStatus.serverError,
    isNotSameSubDomain: errorLinkStatus.notSameSubDomain,
  };

  return (
    <ApolloProvider client={apolloClient}>
      <ErrorLinkHandler {...ErrorLinkHandlerProps}>{children}</ErrorLinkHandler>
    </ApolloProvider>
  );
};

export function App() {
  /**
   * fontawesomeのIconを追加する
   */
  library.add(
    faCaretUp,
    faPen,
    faCheck,
    faCircle,
    faBell,
    faArrowLeft,
    faUserPlus,
    faFile,
    faChevronUp,
    faChevronDown,
    faChevronLeft,
    faChartBar,
    faExclamationCircle,
    faQuestionCircle,
    faSearch,
  );

  return (
    <ApolloProviderWithClient>
      <ThemeProvider theme={defaultTheme}>
        <GlobalStyle />
        <Router />
      </ThemeProvider>
    </ApolloProviderWithClient>
  );
}
